import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a variant-reactive Size & Fit measurements
 * table. Reads an embedded JSON blob keyed by variant id, and swaps the
 * visible cell values when the shopper selects a different variant.
 *
 * Does not declare `requiredRefs` because a given product may only carry
 * "tops" measurements or only "bottoms" measurements — the set of ref
 * elements actually present in the DOM varies per product.
 *
 * @extends {Component}
 */
class SizeFitTableComponent extends Component {
  /** @type {Record<string, Record<string, string>>} */
  #dataMap = {};

  connectedCallback() {
    super.connectedCallback();
    this.#dataMap = this.#parseData();

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @returns {Record<string, Record<string, string>>}
   */
  #parseData() {
    const script = this.querySelector('script[data-size-fit-data]');
    if (!script?.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[size-fit-table] Failed to parse measurement data:', error);
      return {};
    }
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource, productId } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        // Ignore selects bubbling up from an unrelated product on the page
        // (e.g. a different quick-add modal or product card).
        if (productId && this.dataset.productId && String(productId) !== String(this.dataset.productId)) {
          return;
        }

        if (!resource) return;

        const measurements = this.#dataMap[String(resource.id)];
        if (!measurements) return;

        for (const [key, value] of Object.entries(measurements)) {
          const cell = this.querySelector(`[ref="${key}"]`);
          if (!cell) continue;
          cell.textContent = value === '—' || value == null || value === '' ? '—' : `${value}"`;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[size-fit-table] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('size-fit-table-component')) {
  customElements.define('size-fit-table-component', SizeFitTableComponent);
}
